import React, { createContext, useContext, useCallback, useEffect, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserManagementEnabled } from '@/hooks/useUserManagementEnabled';
import {
  SpaceAssignment,
  SpaceType,
  SpaceRelation,
  GET_USER_SPACE_ASSIGNMENTS,
  GetUserSpaceAssignmentsResponse,
  GetUserSpaceAssignmentsVariables,
  AUTH_API_URL,
} from '@/pages/Settings/UserManagement/SpaceAssignments';
import { GET_ME, GetMeQueryResponse } from '@/pages/Settings/UserManagement/Users';
import { isErrorUserNotFound } from '@/api/errors';
import { useUserContext } from './UserContext';

// Query to fetch site hierarchy for parent lookups
const GET_SITE_HIERARCHY_FOR_ACCESS = gql`
  query GetSiteHierarchyForAccess {
    sites {
      data {
        id
        buildings {
          id
          floors {
            id
            rooms {
              id
            }
            zones {
              id
            }
          }
        }
      }
    }
  }
`;

// Types for hierarchy data
interface HierarchyRoom {
  id: string;
}

interface HierarchyZone {
  id: string;
}

interface HierarchyFloor {
  id: string;
  rooms?: HierarchyRoom[];
  zones?: HierarchyZone[];
}

interface HierarchyBuilding {
  id: string;
  floors?: HierarchyFloor[];
}

interface HierarchySite {
  id: string;
  buildings?: HierarchyBuilding[];
}

interface HierarchyResponse {
  sites?: {
    data?: HierarchySite[];
  };
}

// Parent chain for hierarchical access checking
interface ParentChain {
  floor?: string;
  building?: string;
  site?: string;
}

// Relations that grant write access (can modify spaces, sensors, etc.)
const WRITE_RELATIONS: SpaceRelation[] = ['admin', 'member', 'can_edit'];

export interface SpaceAccessContextValue {
  // User's direct space assignments from OpenFGA
  assignments: SpaceAssignment[];
  // Loading state
  isLoading: boolean;
  // Error state
  error: Error | null;
  // Whether user has unrestricted access (admin/superuser, or feature flag off)
  isUnrestricted: boolean;
  // Whether the user has the Admin role in the Auth API (distinct from isUnrestricted)
  isAuthApiAdmin: boolean;
  // Whether the user has the Owner role in the Auth API
  isAuthApiOwner: boolean;
  // The current user's DB user ID (resolved by the `me` query), or null if not yet loaded
  currentUserId: string | null;
  // Check if user has access to a specific space (includes inherited access)
  hasAccess: (spaceType: SpaceType, spaceId: string, relation?: SpaceRelation) => boolean;
  // Check if user has write access to a specific space (admin, member, or can_edit)
  hasWriteAccess: (spaceType: SpaceType, spaceId: string) => boolean;
  // Check if user has any access (is not restricted)
  hasAnyAccess: () => boolean;
  // Get all accessible space IDs for a given type
  getAccessibleSpaceIds: (spaceType: SpaceType) => string[];
  // Resolve the first accessible navigation target — a floor that is either
  // directly accessible or contains an accessible room/zone. Returns the
  // floor id plus an optional roomId / zoneId when the access came from a
  // leaf grant. Returns null when the user has no navigable target.
  getFirstAccessibleTarget: () => {
    floorId: string;
    roomId?: string;
    zoneId?: string;
  } | null;
  // Returns the parent chain (floor/building/site) for a given space id, or
  // undefined when the space isn't in the loaded hierarchy. Used by the
  // route guard to reject URLs that pair a leaf (room/zone) with a
  // mismatched parent floor id.
  getParentChain: (spaceId: string) =>
    | {
        floor?: string;
        building?: string;
        site?: string;
      }
    | undefined;
  // Refetch assignments
  refetch: () => void;
}

const SpaceAccessContext = createContext<SpaceAccessContextValue>({
  assignments: [],
  isLoading: true,
  error: null,
  isUnrestricted: false,
  isAuthApiAdmin: false,
  isAuthApiOwner: false,
  currentUserId: null,
  hasAccess: () => false, // Default to no access for safety
  hasWriteAccess: () => false, // Default to no write access for safety
  hasAnyAccess: () => false,
  getAccessibleSpaceIds: () => [],
  getFirstAccessibleTarget: () => null,
  getParentChain: () => undefined,
  refetch: () => {},
});

export const SpaceAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  const { isSuperUser, clientId } = useUserContext();

  // Only apply space access gating when the user_management feature is enabled.
  // When disabled, all users get unrestricted access (no space-level filtering).
  const isUserManagementEnabled = useUserManagementEnabled();

  // UserContext is still loading if clientId is empty (useUser.tsx hasn't set it yet)
  // Using clientId instead of roles.length because clientId is always set after JWT decode
  const isUserContextLoading = isAuthenticated && !clientId;

  // Fetch current user's Auth API profile via `me` query.
  // No variables needed — the backend resolves the user from the JWT.
  // Uses errorPolicy 'all' so that users not found in the database (e.g. legacy users
  // without a row in the users table) are handled gracefully — data will be null and
  // isAuthApiAdmin will default to false.
  const {
    data: meData,
    loading: isMeQueryLoading,
    error: meError,
  } = useQuery<GetMeQueryResponse>(GET_ME, {
    skip: !isAuthenticated || !isUserManagementEnabled || isSuperUser,
    context: { uri: AUTH_API_URL },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  // The DB user ID resolved by the `me` query — used for all subsequent queries
  const currentUserId = meData?.me?.id ?? null;

  // Two shared predicates for the null-me paths exposed by `errorPolicy: 'all'`.
  // Memoized once so the toast effect AND the isUnrestricted breaker (below)
  // read the same booleans, with deps that only change when the underlying
  // `meError.graphQLErrors` / `meData.me` shapes shift. Apollo can hand us
  // new `meError` / `meData` object references on every poll even when these
  // shapes are stable, so memoizing keeps both consumers from recomputing
  // unnecessarily.
  const isLegacyUserNotFound = useMemo(
    () => !!meError?.graphQLErrors?.some(isErrorUserNotFound),
    [meError],
  );
  const isCleanNullResponse = useMemo(() => !meError && meData?.me === null, [meError, meData]);

  // Differentiate the three null-me paths exposed by `errorPolicy: 'all'`:
  //
  //   1. `meError` carries ERROR_USER_NOT_FOUND (40500) → legacy user with
  //      no idps_users row. The circuit breaker below grants them
  //      unrestricted access, so the user-facing toast would be a lie
  //      ("some pages may not show your spaces" while they actually see
  //      everything). Console-warn only for support visibility.
  //
  //   2. `meError` populated with anything else → genuine auth-api failure
  //      (network / 5xx without 40500 / GraphQL parse error). The user is
  //      authenticated but the breaker does NOT fire — they really will
  //      sit on an empty sidebar. Toast so they know to refresh.
  //
  //   3. No `meError` and `me == null` → the call succeeded with a clean
  //      null response. Future-proof case (current backend doesn't take
  //      this path); console-warn for the same reason as #1.
  //
  // Toast id is stable so retries don't stack duplicates.
  useEffect(() => {
    if (
      !isAuthenticated ||
      !isUserManagementEnabled ||
      isSuperUser ||
      isMeQueryLoading ||
      meData?.me != null
    ) {
      return;
    }
    if (meError && !isLegacyUserNotFound) {
      console.error(
        '[SpaceAccessContext] `me` query failed; user will see no space assignments until retry',
        meError,
      );
      toast.error(
        'Could not load your profile. Some pages may not show your spaces; please refresh to retry.',
        { id: 'space-me-error', duration: 8000 },
      );
    } else {
      console.warn(
        '[SpaceAccessContext] `me` query returned no user (no idps_users row) — space assignments will be skipped.',
      );
    }
  }, [
    isAuthenticated,
    isUserManagementEnabled,
    isSuperUser,
    isMeQueryLoading,
    meData,
    meError,
    isLegacyUserNotFound,
  ]);

  // Check if the current user has the Auth API Owner role
  const isAuthApiOwner = useMemo(() => {
    const authApiRoles = meData?.me?.roles;
    if (!authApiRoles) return false;
    return authApiRoles.some((r) => r.toLowerCase() === 'owner');
  }, [meData]);

  // Check if the current user has the Auth API Admin role (owners also pass admin-level gates)
  const isAuthApiAdmin = useMemo(() => {
    const authApiRoles = meData?.me?.roles;
    if (!authApiRoles) return false;
    return authApiRoles.some((r) => r.toLowerCase() === 'admin' || r.toLowerCase() === 'owner');
  }, [meData]);

  // Check if user has admin or superuser role
  // isSuperUser is set by useUser.tsx from the JWT access token claim 'http://butlr.io/roles'
  // isAuthApiAdmin is set from the Auth API user profile (e.g., roles: ["Admin"])
  // When user_management feature flag is disabled, all users are unrestricted (no space gating)
  const isUnrestricted = useMemo(() => {
    // If UserContext hasn't loaded yet, don't make access decisions
    if (isUserContextLoading) {
      return false;
    }
    // When user_management feature flag is off, bypass space access gating entirely
    if (!isUserManagementEnabled) {
      return true;
    }
    if (isSuperUser || isAuthApiAdmin) {
      return true;
    }
    // Circuit breaker for legacy users: turning the user_management gate
    // ON for an org used to instantly lock out users whose backing
    // idps_users row hadn't been created yet (the typical Brigid
    // scenario). When we can confirm that's the state, fall back to
    // unrestricted access — matching pre-gate behavior — so the gate
    // flip doesn't break pre-existing logged-in users.
    //
    // There are two ways the auth-API surfaces "no idps_users row":
    //
    //   1. **Production path**: `Me` resolver returns
    //      `butlrerror.ERROR_USER_NOT_FOUND` (code 40500). With Apollo
    //      `errorPolicy: 'all'` the frontend sees `meError` populated
    //      AND `meData.me === null`. This is the actual legacy-user
    //      signal in the current backend.
    //
    //   2. **Future-proof path**: a hypothetical backend change where
    //      `Me` returns `(nil, nil)` — clean null, no error. Kept here
    //      so a future resolver refactor doesn't silently regress the
    //      breaker. Matches `meError === undefined && meData.me === null`.
    //
    // Both checks are deliberately strict — no `!meData?.me` looseness
    // that would also match `meData === undefined` (query skipped / in
    // flight / unauthenticated race) or `meData.me === undefined`
    // (unexpected response shape). Anything ambiguous fails closed.
    //
    // ANY other `meError` (network failure, GraphQL parse error, 5xx
    // without a 40500 code) is a transient backend issue — retry on
    // refresh is the right outcome, not silently granting access. The
    // Auth API enforces authorization on every mutation independently,
    // so this is UX hardening, not a security boundary.
    if (!isMeQueryLoading && (isLegacyUserNotFound || isCleanNullResponse)) {
      return true;
    }
    return false;
  }, [
    isSuperUser,
    isAuthApiAdmin,
    isUserContextLoading,
    isUserManagementEnabled,
    isMeQueryLoading,
    isLegacyUserNotFound,
    isCleanNullResponse,
  ]);

  // Fetch user's space assignments from auth API using the DB user ID from `me`
  // Skip when user_management feature flag is disabled (no gating needed)
  const {
    data,
    loading: isQueryLoading,
    error: queryError,
    refetch,
  } = useQuery<GetUserSpaceAssignmentsResponse, GetUserSpaceAssignmentsVariables>(
    GET_USER_SPACE_ASSIGNMENTS,
    {
      variables: { user_id: currentUserId || '' },
      skip: !currentUserId || !isUserManagementEnabled,
      context: { uri: AUTH_API_URL },
      fetchPolicy: 'cache-and-network',
    },
  );

  // Fetch site hierarchy for parent lookups (hierarchical access checking)
  const {
    data: hierarchyData,
    loading: isHierarchyLoading,
    error: hierarchyError,
  } = useQuery<HierarchyResponse>(GET_SITE_HIERARCHY_FOR_ACCESS, {
    skip: !isAuthenticated || isUnrestricted, // Skip if not authenticated or already unrestricted
    fetchPolicy: 'cache-first',
  });

  // Surface hierarchy fetch failures. Without a populated parentLookup,
  // SpaceAccessGuard's leaf→floor match fails closed and redirects a
  // legitimate leaf-only user to `/` with no content rendered. Console
  // trace lets ops see the root cause; toast lets the user see something
  // actionable instead of a blank redirect. Toast `id` is stable so we
  // don't stack duplicates when the query retries.
  useEffect(() => {
    if (hierarchyError) {
      console.error(
        '[SpaceAccessContext] GET_SITE_HIERARCHY_FOR_ACCESS failed; guard will fail closed for leaf-grant users until retry',
        hierarchyError,
      );
      toast.error(
        'Space hierarchy failed to load. Some pages may redirect to the dashboard; please refresh to retry.',
        { id: 'space-hierarchy-error', duration: 8000 },
      );
    }
  }, [hierarchyError]);

  // Build parent lookup maps for hierarchical access checking
  // Maps: spaceId -> ParentChain (parent floor, building, site)
  const parentLookup = useMemo(() => {
    const lookup = new Map<string, ParentChain>();
    const sites = hierarchyData?.sites?.data || [];

    sites.forEach((site) => {
      // Sites have no parents
      lookup.set(site.id, {});

      site.buildings?.forEach((building) => {
        // Buildings have site as parent
        lookup.set(building.id, { site: site.id });

        building.floors?.forEach((floor) => {
          // Floors have building and site as parents
          lookup.set(floor.id, { building: building.id, site: site.id });

          floor.rooms?.forEach((room) => {
            // Rooms have floor, building, and site as parents
            lookup.set(room.id, { floor: floor.id, building: building.id, site: site.id });
          });

          floor.zones?.forEach((zone) => {
            // Zones have floor, building, and site as parents
            lookup.set(zone.id, { floor: floor.id, building: building.id, site: site.id });
          });
        });
      });
    });

    return lookup;
  }, [hierarchyData]);

  const assignments = useMemo(() => {
    return data?.userSpaceAssignments || [];
  }, [data]);

  // Build a lookup map for quick access checks
  // Key format: "spaceType:spaceId" -> Set of relations
  const accessMap = useMemo(() => {
    const map = new Map<string, Set<SpaceRelation>>();
    assignments.forEach((assignment) => {
      const key = `${assignment.space_type}:${assignment.space_id}`;
      if (!map.has(key)) {
        map.set(key, new Set());
      }
      map.get(key)!.add(assignment.relation);
    });
    return map;
  }, [assignments]);

  // Check if user has direct access to a space
  const hasDirectAccess = useCallback(
    (spaceType: SpaceType, spaceId: string, relation?: SpaceRelation): boolean => {
      const key = `${spaceType}:${spaceId}`;
      const relations = accessMap.get(key);
      if (!relations) return false;
      if (!relation) return true; // Any relation counts
      return relations.has(relation);
    },
    [accessMap],
  );

  // Check if user has access through parent hierarchy
  // If user has access to a parent space, they have access to all children
  const hasHierarchicalAccess = useCallback(
    (spaceType: SpaceType, spaceId: string, relation?: SpaceRelation): boolean => {
      // Get parent chain for this space
      const parents = parentLookup.get(spaceId);
      if (!parents) {
        return false;
      }

      // Check access to each parent in the hierarchy
      // Hierarchy: site → building → floor → room/zone
      if (parents.site && hasDirectAccess('site', parents.site, relation)) {
        return true;
      }
      if (parents.building && hasDirectAccess('building', parents.building, relation)) {
        return true;
      }
      if (parents.floor && hasDirectAccess('floor', parents.floor, relation)) {
        return true;
      }

      return false;
    },
    [parentLookup, hasDirectAccess],
  );

  // Check if user has access (direct or inherited through parent hierarchy)
  // Hierarchy: site → building → floor → room/zone
  // If user has access to a parent, they have access to all children
  const hasAccess = useCallback(
    (spaceType: SpaceType, spaceId: string, relation?: SpaceRelation): boolean => {
      // If user is admin or superuser, they have full access
      if (isUnrestricted) {
        return true;
      }
      // If user has no assignments, they have no access
      if (assignments.length === 0) {
        return false;
      }
      // Check direct access first
      if (hasDirectAccess(spaceType, spaceId, relation)) {
        return true;
      }
      // Check hierarchical access through parents
      return hasHierarchicalAccess(spaceType, spaceId, relation);
    },
    [isUnrestricted, assignments, hasDirectAccess, hasHierarchicalAccess],
  );

  // Check if user has write access (admin, member, or can_edit)
  // Write access allows modifying spaces, sensors, configurations, etc.
  const hasWriteAccess = useCallback(
    (spaceType: SpaceType, spaceId: string): boolean => {
      // If user is admin or superuser, they have full write access
      if (isUnrestricted) {
        return true;
      }
      // If user has no assignments, they have no access
      if (assignments.length === 0) {
        return false;
      }
      // Check if user has any write relation (direct or hierarchical)
      return WRITE_RELATIONS.some((relation) => hasAccess(spaceType, spaceId, relation));
    },
    [isUnrestricted, assignments, hasAccess],
  );

  // Check if user has any access at all
  const hasAnyAccess = useCallback((): boolean => {
    // If user is admin or superuser, they have full access
    if (isUnrestricted) {
      return true;
    }
    // If user has any assignments, they have some access
    if (assignments.length > 0) {
      return true;
    }
    // If user has NO assignments and is NOT admin/superuser, they have NO access
    return false;
  }, [isUnrestricted, assignments]);

  // Get all accessible space IDs for a given type
  const getAccessibleSpaceIds = useCallback(
    (spaceType: SpaceType): string[] => {
      return assignments.filter((a) => a.space_type === spaceType).map((a) => a.space_id);
    },
    [assignments],
  );

  // Resolve the first accessible navigation target (floor + optional leaf id).
  // Priority: a directly-granted floor, otherwise the parent floor of the
  // first granted room, otherwise the parent floor of the first granted zone.
  // Returns null when the user has no accessible floor or the hierarchy
  // hasn't loaded yet (parent lookup empty).
  //
  // Within each space-type bucket, assignments are sorted by `space_id` so
  // the chosen leaf is deterministic across reloads regardless of
  // backend-response order.
  //
  // The choice is ARBITRARY BUT STABLE: `space_id` is opaque (not a
  // human-readable name), so "first" here has no product meaning beyond
  // "consistent for a given assignment set." If a user's grants churn
  // (add/revoke), the landing leaf may jump unexpectedly. If product
  // ever wants a semantic ordering (by name / by floor hierarchy), this
  // needs to join with the site hierarchy data.
  const getFirstAccessibleTarget = useCallback(() => {
    if (isUnrestricted) {
      // Unrestricted users have no specific "first target" — caller should
      // pick any floor via its own list (e.g. getFloors()). We intentionally
      // return null so the caller keeps its existing behavior.
      return null;
    }

    const byType = (t: SpaceType) =>
      assignments
        .filter((a) => a.space_type === t)
        .sort((a, b) => a.space_id.localeCompare(b.space_id));

    const directFloorId = byType('floor')[0]?.space_id;
    if (directFloorId) {
      return { floorId: directFloorId };
    }

    const roomGrant = byType('room')[0];
    if (roomGrant) {
      const parents = parentLookup.get(roomGrant.space_id);
      if (parents?.floor) {
        return { floorId: parents.floor, roomId: roomGrant.space_id };
      }
    }

    const zoneGrant = byType('zone')[0];
    if (zoneGrant) {
      const parents = parentLookup.get(zoneGrant.space_id);
      if (parents?.floor) {
        return { floorId: parents.floor, zoneId: zoneGrant.space_id };
      }
    }

    return null;
  }, [assignments, parentLookup, isUnrestricted]);

  // Exposes the internal parentLookup so route-guard logic can verify that
  // a URL's `spaceId` (floor) actually matches the parent floor of the
  // room/zone leaf in the same URL — closing a crafted-URL hole where a
  // user with a leaf grant could otherwise trigger floor-scoped GraphQL
  // reads on an unrelated floor within their org.
  //
  // Returns a fresh object (not the Map's stored reference) so callers
  // can't mutate an entry and poison subsequent lookups.
  const getParentChain = useCallback(
    (spaceId: string) => {
      const chain = parentLookup.get(spaceId);
      return chain ? { ...chain } : undefined;
    },
    [parentLookup],
  );

  const handleRefetch = useCallback(() => {
    if (currentUserId) {
      refetch();
    }
  }, [currentUserId, refetch]);

  const isLoading =
    isAuthLoading ||
    isQueryLoading ||
    isUserContextLoading ||
    isMeQueryLoading ||
    (!isUnrestricted && isHierarchyLoading);

  const error = useMemo(
    () => (queryError ? new Error(queryError.message) : null),
    // Depend on the message, not the queryError object: Apollo re-issues a new
    // ApolloError on every network poll, so keying on the object would churn a
    // fresh Error every tick. Message-only keeps the Error stable across polls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryError?.message],
  );

  const value: SpaceAccessContextValue = useMemo(
    () => ({
      assignments,
      isLoading,
      error,
      isUnrestricted,
      isAuthApiAdmin,
      isAuthApiOwner,
      currentUserId,
      hasAccess,
      hasWriteAccess,
      hasAnyAccess,
      getAccessibleSpaceIds,
      getFirstAccessibleTarget,
      getParentChain,
      refetch: handleRefetch,
    }),
    [
      assignments,
      isLoading,
      error,
      isUnrestricted,
      isAuthApiAdmin,
      isAuthApiOwner,
      currentUserId,
      hasAccess,
      hasWriteAccess,
      hasAnyAccess,
      getAccessibleSpaceIds,
      getFirstAccessibleTarget,
      getParentChain,
      handleRefetch,
    ],
  );

  return <SpaceAccessContext.Provider value={value}>{children}</SpaceAccessContext.Provider>;
};

export const useSpaceAccess = () => {
  const context = useContext(SpaceAccessContext);
  return context;
};

export default SpaceAccessContext;
